import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import cn from 'classnames'
import { message } from 'antd'
import { Button, ContentCard, Grid, Stack } from '@forwardflow/ui-v2'
import {
   ArrowLeftMdIcon,
   CheckCircle02Icon,
   ChevronRightIcon,
   ClockIcon,
   PhoneIcon,
   StopCircle04Icon,
   UsersIcon,
   VolumeMaxIcon,
   ZapIcon,
} from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import CampaignService from '@forwardflow/services/CampaignService'
import PhoneNumberService from '@forwardflow/services/PhoneNumberService'
import { useGetVoices } from '@forwardflow/queries/campaign.queries'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber.ts'
import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { isFreeFlow, useDevTools } from '../dev/devTools'
import { joinNames, stopItems } from './stage-mapping-explained'
import { useLeadCount } from './first-campaign/useLeadCount'
import { FALLBACK_VOICES } from './voice/voices'
import {
   FIRST_CAMPAIGN_NUMBERS,
   ILaunchDeps,
   LAUNCH_STAGES,
   LaunchError,
   launchFirstCampaign,
   TLaunchStage,
} from './start-calling'

const DAY_SHORT: Record<string, string> = {
   Monday: 'Mon',
   Tuesday: 'Tue',
   Wednesday: 'Wed',
   Thursday: 'Thu',
   Friday: 'Fri',
   Saturday: 'Sat',
   Sunday: 'Sun',
}

const clock = (t: string) => {
   const [h, m] = t.split(':').map(Number)
   if (Number.isNaN(h)) return t
   const suffix = h >= 12 ? 'pm' : 'am'
   const hour = h % 12 || 12
   return m ? `${hour}:${String(m).padStart(2, '0')} ${suffix}` : `${hour} ${suffix}`
}

/** "Mon to Fri, 9 am to 6 pm" from the saved business hours. */
const hoursSentence = (days: { day: string; enabled: boolean; startTime: string; endTime: string }[]) => {
   const on = days.filter((d) => d.enabled)
   if (!on.length) return 'Every day'
   const first = on[0]
   const last = on[on.length - 1]
   const sameTimes = on.every((d) => d.startTime === first.startTime && d.endTime === first.endTime)
   const span = on.length === 1 ? DAY_SHORT[first.day] : `${DAY_SHORT[first.day]} to ${DAY_SHORT[last.day]}`
   return sameTimes ? `${span}, ${clock(first.startTime)} to ${clock(first.endTime)}` : span
}

const browserTimezone = () => {
   try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
   } catch {
      return ''
   }
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Step 7: the summary, then one button.
 *
 * Five lines say what will happen: who we call, who takes the appointments,
 * from which number, in which voice, when. "Start calling" creates the
 * campaign, reserves the numbers, creates the assistant and activates, with
 * progress shown in place; a failure says what happened and where to finish.
 * The campaign console is never part of the first campaign.
 */
export const StartCalling = () => {
   const navigate = useNavigate()
   const {
      onboardingData,
      goToStep,
      getPreviousStep,
      setIsOnBoardingCompleted,
      setIsCampaignConfigured,
      setBusinessHours,
   } = useOnboardingStoreV2()
   const {
      campaignConfiguration: config,
      invitedUsers,
      pricingData,
      businessHours,
      stageMappings,
      email,
   } = onboardingData
   const freeFlow = isFreeFlow()
   const { data: voices } = useGetVoices()

   const criteria = config?.campaign_filters?.eligible_criteria || []
   const { count, loading: counting } = useLeadCount(config?.campaign_name ? criteria : null)
   const owner = invitedUsers?.find((u) => u.isOwner)
   const seats = pricingData?.seatsCount ?? 1
   const areaCodes = config?.area_codes || []
   const assistant = config?.ai_assistants?.[0]
   const voice = useMemo(
      () =>
         [...(voices || []), ...FALLBACK_VOICES].find((v) => v.voiceId === assistant?.voice_id) ||
         FALLBACK_VOICES[0],
      [voices, assistant?.voice_id]
   )
   const stops = useMemo(() => stopItems(stageMappings || []), [stageMappings])
   const timezone = businessHours?.timezone || browserTimezone()

   const [stage, setStage] = useState<TLaunchStage | null>(null)
   const [done, setDone] = useState(false)
   const [error, setError] = useState<LaunchError | null>(null)

   const deps: ILaunchDeps = freeFlow
      ? {
           createCampaign: async () => {
              await wait(700)
              return { id: 'free-flow-campaign' }
           },
           searchNumbers: async (code) => {
              await wait(600)
              return Array.from({ length: 3 }, (_, i) => ({
                 phoneNumber: `+1${code || '303'}555010${i}`,
              }))
           },
           reserveNumber: async () => wait(150),
           createAssistants: async () => wait(500),
           activate: async () => {
              await wait(600)
              return { hasFailedPurchase: false }
           },
        }
      : {
           createCampaign: (input) =>
              CampaignService.createCustomActiveLeadSource(
                 input.campaignName,
                 { eligible_criteria: input.criteria },
                 input.campaignType,
                 { bufferTime: input.bufferTime },
                 true,
                 null,
                 input.leadTypes
              ) as Promise<{ id: string } | null | undefined>,
           searchNumbers: async (code) =>
              ((await PhoneNumberService.getAvailablePhoneNumbers({
                 page: 1,
                 limit: 50,
                 search: code,
              })) || []) as never,
           reserveNumber: (campaignId, number) =>
              CampaignService.addCampaignPhoneNumber(campaignId, number as never),
           createAssistants: (campaignId, a) =>
              CampaignService.createOrganizationCampaignAssistantsInBulk(campaignId, [
                 {
                    assistantName: a.assistantName,
                    voiceId: a.voiceId,
                    description: a.description,
                    designation: 'Sales assistant',
                    avatar: a.avatar,
                    previewUrl: a.previewUrl,
                 },
              ]),
           activate: (campaignId) => CampaignService.activateCampaign(campaignId),
        }

   const launch = async () => {
      if (!config?.campaign_name && !freeFlow) {
         goToStep('first-campaign')
         return
      }
      setError(null)
      setStage('campaign')
      try {
         const result = await launchFirstCampaign(
            {
               campaignName: config?.campaign_name || 'First campaign',
               criteria,
               campaignType: config?.campaign_type?.length ? config.campaign_type : ['new-lead'],
               leadTypes: config?.lead_type_filter?.length ? config.lead_type_filter : ['buyer', 'seller'],
               bufferTime: config?.first_to_contact?.buffer_time ?? 0,
               areaCodes,
               assistant: {
                  assistantName: assistant?.assistant_name || voice.name,
                  voiceId: assistant?.voice_id || voice.voiceId,
                  avatar: voice.avatar,
                  description: `Hello I am ${assistant?.assistant_name || voice.name}, your real estate assistant`,
                  previewUrl: voice.previewUrl,
               },
            },
            { ...deps, onStage: setStage }
         )
         if (!businessHours?.timezone && timezone) {
            setBusinessHours({ ...businessHours, timezone })
         }
         setIsCampaignConfigured(true)
         if (!isOperatorEmail(email || '')) setIsOnBoardingCompleted(true)
         setDone(true)
         if (result.hasFailedPurchase) {
            message.warning(
               'The campaign is live, but one number could not be purchased. Check Phone Numbers.'
            )
         } else if (result.emptyAreaCodes.length) {
            message.info(
               `No numbers in (${result.emptyAreaCodes.join(', ')}); we used the next best area code.`
            )
         }
         navigate('/dashboard')
      } catch (e) {
         const err =
            e instanceof LaunchError
               ? e
               : new LaunchError(stage || 'campaign', "That didn't work. Try again.")
         setError(err)
         setStage(null)
      }
   }

   useDevTools({
      skip: () => {
         setIsCampaignConfigured(true)
         setIsOnBoardingCompleted(true)
         navigate('/dashboard')
         return true
      },
   })

   const editLink = (step: TOnboardingStep) => (
      <button
         type="button"
         className="text-[#A684FF] text-sm font-medium hover:underline shrink-0"
         onClick={() => goToStep(step)}
         disabled={Boolean(stage)}
      >
         Edit
      </button>
   )

   const Row = ({
      icon,
      children,
      step,
      testId,
   }: {
      icon: React.ReactNode
      children: React.ReactNode
      step?: TOnboardingStep
      testId?: string
   }) => (
      <li className="flex items-start gap-4 py-3 border-b border-white/10 last:border-b-0" data-testid={testId}>
         <span className="flex shrink-0 items-center justify-center w-9 h-9 rounded-[8px] border border-white/10 bg-white/[0.03] text-[#A684FF]">
            {icon}
         </span>
         <div className="flex-1 min-w-0 text-white text-sm leading-6">{children}</div>
         {step && editLink(step)}
      </li>
   )

   const launching = Boolean(stage) && !done
   const stageIndex = LAUNCH_STAGES.findIndex((s) => s.id === stage)

   return (
      <Stack gap={24}>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-semibold text-lg">Here's what happens when you press start</p>
            <ul className="divide-y-0" data-testid="launch-summary">
               <Row icon={<ZapIcon width={18} />} step="first-campaign" testId="row-who">
                  We call{' '}
                  <b>
                     {counting && count === undefined
                        ? 'your'
                        : count !== undefined
                          ? `${formatNumber(count)}`
                          : 'your'}
                  </b>{' '}
                  {count === 1 ? 'lead' : 'leads'} in{' '}
                  <b>{config?.campaign_name || 'your first campaign'}</b>
                  {config?.campaign_type?.includes('new-lead') && (
                     <span className="text-[#99A1AF]">, new ones within {config?.first_to_contact?.buffer_time || 2} minutes of arriving</span>
                  )}
                  .
               </Row>
               <Row icon={<UsersIcon width={18} />} step="you-and-team" testId="row-team">
                  Appointments go to{' '}
                  <b>
                     {seats > 1
                        ? `${owner?.firstName || 'you'} and ${seats - 1} teammate${seats - 1 === 1 ? '' : 's'}`
                        : owner?.firstName
                          ? `${owner.firstName} (you)`
                          : 'you'}
                  </b>
                  {seats > 1 && <span className="text-[#99A1AF]">, round robin</span>}, and land in
                  Follow Up Boss tagged ff-appointment-set.
               </Row>
               <Row icon={<PhoneIcon width={18} />} step="number-and-stops" testId="row-number">
                  Calls come from{' '}
                  <b>
                     {areaCodes.length
                        ? `${FIRST_CAMPAIGN_NUMBERS} local numbers in ${areaCodes.map((c) => `(${c})`).join(', ')}`
                        : `${FIRST_CAMPAIGN_NUMBERS} local numbers we pick for you`}
                  </b>
                  . First 3 are on us.
               </Row>
               <Row icon={<StopCircle04Icon width={18} />} step="number-and-stops" testId="row-stops">
                  We stop once a lead reaches{' '}
                  <b>{stops.length ? joinNames(stops) : 'a stop stage you pick'}</b>.
               </Row>
               <Row icon={<VolumeMaxIcon width={18} />} step="voice" testId="row-voice">
                  Leads hear <b>{assistant?.assistant_name || voice.name}</b>
                  {assistant?.assistant_name && assistant.assistant_name !== voice.name && (
                     <span className="text-[#99A1AF]"> ({voice.name}'s voice)</span>
                  )}
                  .
               </Row>
               <Row icon={<ClockIcon width={18} />} testId="row-hours">
                  Calling hours: <b>{hoursSentence(businessHours?.days || [])}</b>
                  {timezone && <span className="text-[#99A1AF]"> ({timezone.replace(/_/g, ' ')})</span>}.
                  Change them under Settings → Business hours.
               </Row>
            </ul>
         </ContentCard>

         {launching && (
            <ContentCard
               className="space-y-3"
               initial={{ opacity: 0, y: 10 }}
               animate={{ opacity: 1, y: 0 }}
               data-testid="launch-progress"
            >
               {LAUNCH_STAGES.map((s, i) => {
                  const state = i < stageIndex ? 'done' : i === stageIndex ? 'now' : 'next'
                  return (
                     <div key={s.id} className="flex items-center gap-3 text-sm">
                        {state === 'done' ? (
                           <CheckCircle02Icon width={18} height={18} color="#00D492" />
                        ) : (
                           <span
                              className={cn(
                                 'w-[18px] h-[18px] rounded-full border-2',
                                 state === 'now'
                                    ? 'border-[#A684FF] border-t-transparent animate-spin'
                                    : 'border-white/15'
                              )}
                           />
                        )}
                        <span className={cn(state === 'next' ? 'text-white/40' : 'text-white')}>
                           {s.label}
                        </span>
                     </div>
                  )
               })}
            </ContentCard>
         )}

         {error && (
            <ContentCard
               className="space-y-3 border border-[#FF8904]/40"
               initial={{ opacity: 0, y: 10 }}
               animate={{ opacity: 1, y: 0 }}
               data-testid="launch-error"
            >
               <p className="text-white font-medium">{error.message}</p>
               <p className="text-[#99A1AF] text-sm">
                  {error.stage === 'numbers'
                     ? 'Pick another area code, or finish in the campaign console where you can choose any number.'
                     : 'Nothing was charged twice. Try again, or finish in the campaign console.'}
               </p>
               <div className="flex flex-wrap gap-3">
                  <Button size="middle" onClick={() => void launch()}>
                     Try again
                  </Button>
                  {error.stage === 'numbers' && (
                     <Button size="middle" variant="secondary" onClick={() => goToStep('number-and-stops')}>
                        Change area code
                     </Button>
                  )}
                  {error.campaignId && (
                     <Button
                        size="middle"
                        variant="secondary"
                        onClick={() => {
                           setIsOnBoardingCompleted(true)
                           navigate(
                              `/campaign/${encodeURIComponent(error.campaignId!)}/phone-number?campaignName=${encodeURIComponent(config?.campaign_name || '')}`
                           )
                        }}
                     >
                        Finish in campaign console
                     </Button>
                  )}
               </div>
            </ContentCard>
         )}

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="space-y-2"
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                     disabled={launching}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={() => void launch()}
                     loading={launching}
                     disabled={launching || done}
                     data-testid="start-calling"
                  >
                     {done ? 'Calling' : launching ? 'Starting' : 'Start calling'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
            <p className="text-center text-xs text-white/40">
               Texting switches on after carrier approval, usually 3 to 5 days.
               Appointments land in Follow Up Boss; connect a calendar any time.
            </p>
         </motion.div>
      </Stack>
   )
}
